<template>
  <div class="duplicate-leads-report">
    <div class="bg-white">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">Duplicate Telefon Numarası Raporu</h1>

      <!-- Filters Section -->
      <div class="filters-container mb-6">
        <div class="flex gap-4 items-end">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Minimum Tekrar Sayısı
            </label>
            <select
              v-model="minCount"
              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option :value="2">2+ tekrar</option>
              <option :value="3">3+ tekrar</option>
              <option :value="5">5+ tekrar</option>
              <option :value="10">10+ tekrar</option>
            </select>
          </div>
          <div>
            <button
              @click="loadReport"
              :disabled="loading"
              class="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="loading">Yükleniyor...</span>
              <span v-else>Raporu Getir</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div v-if="reportData && !loading" class="mb-6">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="text-sm font-medium text-red-600">Toplam Duplicate Grup</div>
            <div class="text-2xl font-bold text-red-900">{{ reportData.summary.total_duplicate_groups }}</div>
          </div>
          <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div class="text-sm font-medium text-orange-600">Toplam Etkilenen Lead</div>
            <div class="text-2xl font-bold text-orange-900">{{ reportData.summary.total_duplicate_leads }}</div>
          </div>
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div class="text-sm font-medium text-blue-600">Minimum Tekrar</div>
            <div class="text-2xl font-bold text-blue-900">{{ reportData.summary.min_count }}+</div>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div v-if="reportData && !loading && reportData.data.length > 0" class="flex justify-between items-center mb-4">
        <div class="text-sm text-gray-700">
          Toplam {{ reportData.data.length }} duplicate grup bulundu.
          {{ (currentPage - 1) * pageSize + 1 }} - {{ Math.min(currentPage * pageSize, reportData.data.length) }} arası gösteriliyor.
        </div>
        <div class="flex gap-2">
          <button
            @click="previousPage"
            :disabled="currentPage === 1"
            class="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Önceki
          </button>
          <span class="px-3 py-1">
            Sayfa {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            @click="nextPage"
            :disabled="currentPage === totalPages"
            class="px-3 py-1 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Sonraki
          </button>
        </div>
      </div>

      <!-- Report Content -->
      <div v-if="reportData && !loading && reportData.data.length > 0" class="report-content space-y-4">
        <div
          v-for="(group, index) in paginatedData"
          :key="index"
          class="bg-white border border-gray-200 rounded-lg overflow-hidden"
        >
          <!-- Group Header -->
          <div class="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <div class="flex items-center gap-3">
              <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-800 font-bold text-sm">
                {{ group.count }}
              </span>
              <div>
                <span class="font-semibold text-gray-900">Telefon: </span>
                <span class="font-mono text-gray-700">{{ formatPhone(group.phone) }}</span>
              </div>
            </div>
            <button
              @click="toggleGroup(index)"
              class="text-sm text-blue-600 hover:text-blue-800"
            >
              {{ expandedGroups.includes(index) ? 'Gizle' : 'Göster' }}
            </button>
          </div>

          <!-- Group Leads Table -->
          <div v-show="expandedGroups.includes(index)" class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ad Soyad</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telefon 2</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mobil</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Temsilci</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kayıt Tarihi</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr
                  v-for="lead in group.leads"
                  :key="lead.id"
                  class="hover:bg-gray-50"
                >
                  <td class="px-4 py-2 whitespace-nowrap text-sm">
                    <a
                      :href="`/leadDetail/${lead.id}`"
                      target="_blank"
                      class="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                    >
                      #{{ lead.id }}
                      <svg xmlns="http://www.w3.org/2000/svg" class="inline w-3 h-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </td>
                  <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{{ lead.name }}</td>
                  <td class="px-4 py-2 whitespace-nowrap text-sm font-mono text-gray-600">{{ lead.phone || '-' }}</td>
                  <td class="px-4 py-2 whitespace-nowrap text-sm font-mono text-gray-600">{{ lead.phone_2 || '-' }}</td>
                  <td class="px-4 py-2 whitespace-nowrap text-sm font-mono text-gray-600">{{ lead.mobile || '-' }}</td>
                  <td class="px-4 py-2 whitespace-nowrap">
                    <span class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                      {{ lead.status_name || 'Bilinmiyor' }}
                    </span>
                  </td>
                  <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-600">{{ lead.agent_name || '-' }}</td>
                  <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{{ formatDate(lead.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-2 text-gray-600">Rapor yükleniyor...</span>
      </div>

      <!-- Empty State -->
      <div v-if="reportData && !loading && reportData.data.length === 0" class="text-center py-12">
        <div class="text-gray-500 text-lg mb-2">Duplicate kayıt bulunamadı</div>
        <div class="text-gray-400">Seçili kriterlere göre tekrar eden telefon numarası bulunmamaktadır.</div>
      </div>

      <!-- Initial State -->
      <div v-if="!reportData && !loading && !hasSearched" class="text-center py-12">
        <div class="text-gray-400">Minimum tekrar sayısı seçerek raporu getirin.</div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../services/http'

export default {
  name: 'DuplicateLeadsReport',
  data() {
    return {
      minCount: 2,
      reportData: null,
      loading: false,
      hasSearched: false,
      currentPage: 1,
      pageSize: 20,
      expandedGroups: []
    }
  },
  computed: {
    totalPages() {
      if (!this.reportData || !this.reportData.data) return 1
      return Math.ceil(this.reportData.data.length / this.pageSize)
    },
    paginatedData() {
      if (!this.reportData || !this.reportData.data) return []
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.reportData.data.slice(start, end)
    }
  },
  methods: {
    async loadReport() {
      this.loading = true
      this.hasSearched = true
      this.currentPage = 1
      this.expandedGroups = []

      try {
        const response = await http.getDuplicatePhonesReport({
          min_count: this.minCount
        })

        if (response.data.success) {
          this.reportData = response.data
          // İlk 5 grubu otomatik aç
          this.expandedGroups = [0, 1, 2, 3, 4]
        } else {
          alert(response.data.message || 'Bir hata oluştu')
          this.reportData = null
        }
      } catch (error) {
        console.error('Report loading error:', error)
        alert('Rapor yüklenirken bir hata oluştu')
        this.reportData = null
      } finally {
        this.loading = false
      }
    },

    formatPhone(phone) {
      if (!phone) return '-'
      // Telefon numarasını formatla
      const cleaned = phone.replace(/\D/g, '')
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`
      } else if (cleaned.length === 11) {
        return `${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`
      }
      return phone
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    toggleGroup(index) {
      const pos = this.expandedGroups.indexOf(index)
      if (pos > -1) {
        this.expandedGroups.splice(pos, 1)
      } else {
        this.expandedGroups.push(index)
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        // Yeni sayfadaki ilk 5 grubu aç
        this.expandedGroups = [0, 1, 2, 3, 4]
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        // Yeni sayfadaki ilk 5 grubu aç
        this.expandedGroups = [0, 1, 2, 3, 4]
      }
    }
  }
}
</script>

<style scoped>
.duplicate-leads-report {
  padding: 1.5rem;
}
</style>
